/**
 * OperatorRightRail — narrow right column for secondary content.
 * Cards stack inside. Hidden on mobile (under 1024px).
 */
import { tokens } from "./tokens";

export function RailCard({
  title,
  children,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        fontFamily: tokens.font.body,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          fontSize: 12,
          fontWeight: 600,
          color: tokens.color.textPrimary,
          borderBottom: `1px solid ${tokens.color.border}`,
        }}
      >
        {title}
      </div>
      <div style={{ padding: "10px 12px" }}>{children}</div>
      {footer && (
        <div style={{ padding: "7px 12px", borderTop: `1px solid ${tokens.color.border}`, fontSize: 11, color: tokens.color.textMuted }}>
          {footer}
        </div>
      )}
    </div>
  );
}

export function PipelineByStage({
  rows,
  total,
  onRowClick,
}: {
  rows: { stage: string; count: number; color?: string }[];
  total?: number;
  /** BUILD-14: optional click handler for drill-down. Receives stage name. */
  onRowClick?: (stage: string) => void;
}) {
  const max = total ?? Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: tokens.font.body }}>
      {rows.map((r) => {
        const pct = (r.count / max) * 100;
        const color = r.color || tokens.color.navy;
        const clickable = !!onRowClick;
        return (
          <div
            key={r.stage}
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: clickable ? "pointer" : undefined, borderRadius: 4, padding: "2px 0" }}
            onClick={clickable ? () => onRowClick!(r.stage) : undefined}
            title={clickable ? `Click to filter pipeline by ${r.stage}` : undefined}
            onMouseEnter={(e) => { if (clickable) (e.currentTarget as HTMLDivElement).style.background = "#F5F5F5"; }}
            onMouseLeave={(e) => { if (clickable) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
          >
            <div style={{ width: 80, fontSize: 12, color: tokens.color.textMuted }}>{r.stage}</div>
            <div style={{ flex: 1, height: 8, background: "#EFEFEE", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: color }} />
            </div>
            <div style={{ width: 24, textAlign: "right", fontSize: 12, fontWeight: 600, color: tokens.color.textPrimary, fontVariantNumeric: "tabular-nums" }}>
              {r.count}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ActivityFeed({
  items,
}: {
  items: {
    text: string;
    sub?: string;
    color?: string; // dot color
    time?: string;
  }[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: tokens.font.body }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              marginTop: 6,
              width: 7, height: 7, borderRadius: "50%",
              background: it.color || tokens.color.black,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: tokens.color.textPrimary, lineHeight: 1.45 }}>{it.text}</div>
            {(it.sub || it.time) && (
              <div style={{ fontSize: 11, color: tokens.color.textMuted, marginTop: 2 }}>
                {it.sub}
                {it.sub && it.time ? " · " : ""}
                {it.time}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OperatorRightRail({
  children,
  width = 300,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <aside
      className="hidden xl:flex"
      style={{
        flexDirection: "column",
        gap: 10,
        width,
        flexShrink: 0,
        fontFamily: tokens.font.body,
      }}
    >
      {children}
    </aside>
  );
}
